/**
 * OfferDetailPage
 * Shows full offer detail with user info and payment options table
 * Route: /credit/offers/:offerId?directoryId=xxx
 */

import { useParams, useSearchParams, Link } from 'react-router-dom';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { CreditLevelBadge } from '@/features/credit/components/CreditLevelBadge';
import { OfferOptionsTable } from '@/features/credit/components/OfferOptionsTable';
import { FeeBreakdown } from '@/features/credit/components/FeeBreakdown';
import { useOfferDetail } from '@/features/credit/hooks';
import { formatCurrency } from '@/utils/formatters';
import { ROUTES } from '@/constants';
import { HelpButton } from '@/features/help';
import { CreditLevel } from '@/types/credit.types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

function getStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'CONVERTED':
    case 'ACCEPTED':
      return 'success';
    case 'VIEWING':
      return 'info';
    case 'LOCKED':
      return 'warning';
    case 'EXPIRED':
      return 'danger';
    case 'ABANDONED':
      return 'default';
    default:
      return 'default';
  }
}

function formatDateShort(dateStr: string): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function InfoRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="flex justify-between py-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium tabular-nums text-foreground">{value ?? '-'}</span>
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function DetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={`card-skeleton-${i.toString()}`} className="rounded-lg border border-border bg-card p-5">
            <Skeleton variant="text" width="50%" height={16} />
            <div className="mt-4 space-y-3">
              {Array.from({ length: 4 }).map((_, j) => (
                <Skeleton key={`row-${j.toString()}`} variant="text" height={14} />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-border bg-card p-6">
        <Skeleton variant="text" width="30%" height={18} />
        <div className="mt-4">
          <Skeleton variant="rectangular" height={160} />
        </div>
      </div>
    </div>
  );
}

// ─── Error State ──────────────────────────────────────────────────────────────

function DetailError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-16">
      <AlertTriangle size={48} className="mb-4 text-muted-foreground" />
      <h3 className="text-lg font-semibold text-foreground">
        Error al cargar la oferta
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Hubo un error al obtener los datos. Intenta de nuevo.
      </p>
      <Button className="mt-4" onClick={onRetry}>
        <RefreshCw size={16} />
        Reintentar
      </Button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function OfferDetailPage() {
  const { offerId = '' } = useParams<{ offerId: string }>();
  const [searchParams] = useSearchParams();
  const directoryId = searchParams.get('directoryId') ?? '';

  const { offer, user, isLoading, isError, refetch } = useOfferDetail(offerId, directoryId);

  const breadcrumbs = [
    { label: 'Inicio', path: ROUTES.DASHBOARD },
    { label: 'Originacion', path: ROUTES.PIPELINE_MATRIX },
    { label: 'Ofertas', path: ROUTES.OFFERS_LIST },
    { label: `Oferta #${offerId.slice(0, 8)}`, path: '#' },
  ];

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title={`Detalle de Oferta #${offerId.slice(0, 8)}`}
          breadcrumbs={breadcrumbs}
        />
        <DetailSkeleton />
      </div>
    );
  }

  if (isError || !offer) {
    return (
      <div>
        <PageHeader
          title={`Detalle de Oferta #${offerId.slice(0, 8)}`}
          breadcrumbs={breadcrumbs}
        />
        <DetailError onRetry={() => refetch()} />
      </div>
    );
  }

  const levelIsValid = Object.values(CreditLevel).includes(offer.credit_level as CreditLevel);

  return (
    <div>
      <PageHeader
        title={`Detalle de Oferta #${offer.offer_id.slice(0, 8)}`}
        breadcrumbs={breadcrumbs}
        actions={
          <div className="flex items-center gap-2">
            <HelpButton pageKey="offer-detail" />
            <Link to={`/credit/profiles/${directoryId}`}>
              <Button variant="outline">Ver Perfil Financiero</Button>
            </Link>
            <Button onClick={() => refetch()}>
              <RefreshCw size={16} />
              Actualizar
            </Button>
          </div>
        }
      />

      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Card: Usuario */}
          <Card title="Usuario">
            <div className="space-y-1">
              {user && (
                <>
                  <InfoRow label="Telefono" value={user.phone_number} />
                  <InfoRow label="Directory ID" value={user.directory_id} />
                  <InfoRow label="People ID" value={user.people_id} />
                  <div className="mt-3">
                    <Link
                      to={`/credit/profiles/${user.directory_id}`}
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      Ver Perfil Financiero
                    </Link>
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Card: Oferta */}
          <Card title="Oferta">
            <div className="space-y-1">
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm text-muted-foreground">Estado</span>
                <Badge variant={getStatusVariant(offer.status)}>{offer.status}</Badge>
              </div>
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm text-muted-foreground">Nivel</span>
                {levelIsValid ? (
                  <CreditLevelBadge level={offer.credit_level as CreditLevel} size="sm" />
                ) : (
                  <span className="text-sm text-muted-foreground">{offer.credit_level}</span>
                )}
              </div>
              <InfoRow label="Monto Minimo" value={formatCurrency(offer.min_amount_mxn, 'MXN', 'es-MX')} />
              <InfoRow label="Monto Maximo" value={formatCurrency(offer.max_amount_mxn, 'MXN', 'es-MX')} />
              {offer.available_rates && offer.available_rates.length > 0 && (
                <div className="mt-2 border-t border-border pt-2">
                  <span className="text-xs text-muted-foreground">Plazos Disponibles</span>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {offer.available_rates.map((r) => (
                      <span
                        key={r.term_weeks}
                        className="inline-flex items-center rounded-md border border-border bg-muted/50 px-2 py-0.5 text-xs tabular-nums"
                      >
                        {r.term_weeks} sem · {(r.annual_rate * 100).toFixed(0)}%
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-2 border-t border-border pt-2">
                <InfoRow label="Expira" value={formatDateShort(offer.offer_expires_at)} />
                <InfoRow label="Creada" value={formatDateShort(offer.created_at)} />
                <InfoRow label="Actualizada" value={formatDateShort(offer.updated_at)} />
              </div>
            </div>
          </Card>
        </div>

        {/* Client selection (shown when offer has been locked/accepted/converted) */}
        {offer.selected_term_weeks != null && (() => {
          const selectedOption = (offer.options ?? []).find(
            (o) => o.term_weeks === offer.selected_term_weeks
          );
          const requestedAmount =
            selectedOption?.requested_amount_mxn ?? offer.selected_amount_mxn;
          return (
            <Card title="Seleccion del Cliente" description="Plan elegido por el cliente">
              <div className="grid grid-cols-2 gap-x-8 gap-y-1 sm:grid-cols-3">
                <InfoRow label="Plazo elegido" value={`${offer.selected_term_weeks} semanas`} />
                <InfoRow label="Tasa aplicada" value={offer.selected_rate != null ? `${(offer.selected_rate * 100).toFixed(1)}%` : '-'} />
                <InfoRow label="Pago semanal" value={offer.weekly_payment_mxn != null ? formatCurrency(offer.weekly_payment_mxn, 'MXN', 'es-MX') : '-'} />
                <InfoRow label="Total a pagar" value={offer.total_to_pay_mxn != null ? formatCurrency(offer.total_to_pay_mxn, 'MXN', 'es-MX') : '-'} />
                <InfoRow label="Total intereses" value={offer.total_interest_mxn != null ? formatCurrency(offer.total_interest_mxn, 'MXN', 'es-MX') : '-'} />
                {offer.accepted_at && <InfoRow label="Aceptada" value={formatDateShort(offer.accepted_at)} />}
                {offer.abandoned_at && <InfoRow label="Abandonada" value={formatDateShort(offer.abandoned_at)} />}
              </div>
              <div className="mt-4 border-t border-border pt-3">
                <p className="mb-2 text-xs font-medium text-muted-foreground">
                  Desglose del monto financiado
                </p>
                <FeeBreakdown
                  requestedAmountMxn={requestedAmount}
                  openingFeeMxn={selectedOption?.opening_fee_mxn}
                  openingFeeIvaMxn={selectedOption?.opening_fee_iva_mxn}
                  openingFeePct={selectedOption?.opening_fee_pct}
                  financedAmountMxn={selectedOption?.financed_amount_mxn}
                />
              </div>
            </Card>
          );
        })()}

        {/* Options table */}
        <Card title="Opciones de Pago" description="Planes de pago disponibles para esta oferta">
          <OfferOptionsTable options={offer.options ?? []} />
        </Card>
      </div>
    </div>
  );
}
